import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Wifi, Loader2, Plug, ShieldCheck, Trash2,
  CheckCircle2, AlertCircle, Cloud, Server, ArrowRight,
} from 'lucide-react';

const CLOUD_DEFAULT_BASE = 'https://api.untill.com/shield/api/v1';
const SELF_DEFAULT_BASE = 'https://untill.mycompany.local:8443/shield/api/v1';

export default function UnTillInstance() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [instance, setInstance] = useState({ configured: false });

  const [form, setForm] = useState({
    instance_type: 'cloud',
    base_url: CLOUD_DEFAULT_BASE,
    tenant_id: '',
    username: '',
    password: '',
    app_token: '',
    app_name: 'SyncPoint',
    site_label: '',
  });

  const refresh = async () => {
    try {
      const r = await api.getUnTillInstance(token);
      setInstance(r.data);
      if (r.data?.configured) {
        setForm((f) => ({
          ...f,
          instance_type: r.data.instance_type || 'cloud',
          base_url: r.data.base_url || CLOUD_DEFAULT_BASE,
          tenant_id: r.data.tenant_id || '',
          username: r.data.username || '',
          app_name: r.data.app_name || 'SyncPoint',
          site_label: r.data.site_label || '',
        }));
      }
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleType = (t) => {
    setForm((f) => ({
      ...f,
      instance_type: t,
      base_url: t === 'cloud' ? CLOUD_DEFAULT_BASE : SELF_DEFAULT_BASE,
    }));
  };

  const handleSave = async () => {
    const missing = ['base_url','tenant_id','username','password'].filter((k)=>!String(form[k]||'').trim());
    if (missing.length) { toast.error(`Missing: ${missing.join(', ')}`); return; }
    setSaving(true);
    try {
      await api.saveUnTillInstance(token, form);
      toast.success('Instance saved. Now click Test connection.');
      setForm((f) => ({ ...f, password: '', app_token: '' }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.testUnTillInstance(token);
      toast.success(`Connected to ${r.data.identifier}`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Connection test failed';
      toast.error(detail);
      await refresh();
    } finally { setTesting(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect this unTill instance? Saved credentials will be removed.')) return;
    setDisconnecting(true);
    try {
      await api.deleteUnTillInstance(token);
      toast.success('Instance disconnected');
      setInstance({ configured: false });
      setForm({ instance_type: 'cloud', base_url: CLOUD_DEFAULT_BASE, tenant_id: '', username: '', password: '', app_token: '', app_name: 'SyncPoint', site_label: '' });
    } catch (e) { toast.error('Failed to disconnect'); }
    finally { setDisconnecting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-blue-400"/></div>;

  const step1 = !!instance.configured;
  const step2 = !!instance.verified;
  const cloud = form.instance_type === 'cloud';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="untill-instance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Wifi className="w-6 h-6 text-blue-400"/>
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">Connect your UnTill POS</h1>
            <p className="text-slate-400 mt-1">The one required integration — SyncPoint reads your sales, products and payments from here.</p>
          </div>
        </div>
        {step2 ? (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5"/> Connected · {instance.site_label || instance.tenant_id}
          </Badge>
        ) : (
          <Badge className="bg-slate-500/15 text-slate-400 border-slate-500/30 gap-1.5">
            <AlertCircle className="w-3.5 h-3.5"/> Not connected
          </Badge>
        )}
      </div>

      {/* Step 1 — Instance type */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="untill-step1">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">1</span>
            Where is your unTill running?
          </CardTitle>
          <CardDescription className="text-slate-400">Pick the option that matches how you access your unTill Back Office today.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => handleType('cloud')} data-testid="type-cloud"
              className={`p-4 rounded-lg border text-left transition-all ${cloud ? 'border-blue-500/60 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'}`}>
              <div className="flex items-center gap-2 mb-1"><Cloud className="w-4 h-4 text-blue-400"/><span className="text-white font-medium">Cloud-hosted (unTill Online)</span></div>
              <p className="text-xs text-slate-400">You log in through a URL like <span className="font-mono">https://something.untill.com</span> from any browser.</p>
            </button>
            <button onClick={() => handleType('self_hosted')} data-testid="type-self"
              className={`p-4 rounded-lg border text-left transition-all ${!cloud ? 'border-blue-500/60 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'}`}>
              <div className="flex items-center gap-2 mb-1"><Server className="w-4 h-4 text-blue-400"/><span className="text-white font-medium">Self-hosted / on-premise</span></div>
              <p className="text-xs text-slate-400">unTill runs on a computer in your restaurant. You access it on a local address like <span className="font-mono">192.168.x.x</span>.</p>
            </button>
          </div>
          {!cloud && (
            <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 leading-relaxed">
              <strong>Heads-up:</strong> for SyncPoint to reach your on-premise server, that server must be reachable from the internet — usually via a VPN, port-forwarding rule, or IP allowlist. Your IT person or unTill installer can help set this up.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Credentials */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="untill-step2">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">2</span>
            Enter your connection details
          </CardTitle>
          <CardDescription className="text-slate-400">
            All five fields are required. If you don&apos;t know a field, see the instructions below — or ask your unTill contact/installer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-slate-300">A friendly name for this venue <span className="text-slate-500">(optional)</span></Label>
              <Input value={form.site_label} onChange={(e)=>setForm({...form, site_label: e.target.value})}
                placeholder="e.g. Café de Vondel — Amsterdam"
                className="bg-slate-800/50 border-slate-700 text-white" data-testid="untill-site-label"/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">API base URL</Label>
              <Input value={form.base_url} onChange={(e)=>setForm({...form, base_url: e.target.value})}
                placeholder={cloud ? CLOUD_DEFAULT_BASE : SELF_DEFAULT_BASE}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" data-testid="untill-base-url"/>
              <p className="text-xs text-slate-500">
                {cloud
                  ? 'Usually ends in /shield/api/v1. Ask unTill support if unsure.'
                  : 'Your on-prem server\'s reachable URL — e.g. https://untill.yourvenue.com/shield/api/v1'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Tenant / database name</Label>
              <Input value={form.tenant_id} onChange={(e)=>setForm({...form, tenant_id: e.target.value})}
                placeholder="e.g. MYCAFE or PROD"
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" data-testid="untill-tenant"/>
              <p className="text-xs text-slate-500">Sometimes called your unTill &quot;database&quot;. Shown in Back Office → About.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Username</Label>
              <Input value={form.username} onChange={(e)=>setForm({...form, username: e.target.value})}
                placeholder="Your unTill Back Office login"
                className="bg-slate-800/50 border-slate-700 text-white text-sm" data-testid="untill-username"/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Password</Label>
              <Input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})}
                placeholder={instance.configured ? '•••••••• (already saved — paste to change)' : ''}
                className="bg-slate-800/50 border-slate-700 text-white text-sm" data-testid="untill-password"/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">AppToken <span className="text-slate-500 text-xs">(optional — leave blank for demo/testing)</span></Label>
              <Input type="password" value={form.app_token} onChange={(e)=>setForm({...form, app_token: e.target.value})}
                placeholder={instance.configured ? '•••••••• (already saved)' : 'Blank is OK for demo — required only for production sync'}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" data-testid="untill-app-token"/>
              <p className="text-xs text-slate-500">Issued once by unTill to authorise SyncPoint as an integrator. Live syncs need it — POS demo runs work without it.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">AppName</Label>
              <Input value={form.app_name} onChange={(e)=>setForm({...form, app_name: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white text-sm" data-testid="untill-app-name"/>
              <p className="text-xs text-slate-500">Should match the name registered with your AppToken.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving}
              className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="untill-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <ShieldCheck className="w-4 h-4 mr-2"/>}
              {instance.configured ? 'Update details' : 'Save details'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 — Test */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="untill-step3">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">3</span>
            Test the connection
          </CardTitle>
          <CardDescription className="text-slate-400">
            We&apos;ll make one read-only call to your unTill instance and read back what we found — so you can confirm this is the right venue before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {instance.last_verify_error && !instance.verified && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
              <strong className="text-rose-300">Last attempt failed:</strong> {instance.last_verify_error}
              {String(instance.last_verify_error).toLowerCase().match(/(timed|reach|refused|resolve)/) && (
                <p className="mt-1 text-rose-200/80">
                  This looks like a network issue — SyncPoint couldn&apos;t reach that address. If your unTill runs on a local network, ask your installer to expose it to the internet (VPN, port-forward, or IP allowlist).
                </p>
              )}
              {String(instance.last_verify_error).toLowerCase().match(/(unauth|forbidden|401|403|invalid.*credentials)/) && (
                <p className="mt-1 text-rose-200/80">
                  This looks like an authentication problem — double-check the username, password, and AppToken in step 2.
                </p>
              )}
            </div>
          )}
          {instance.verified && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              <div className="font-medium text-emerald-100">Connection verified</div>
              <div className="text-xs mt-0.5">
                Last check: {instance.last_verified_at ? new Date(instance.last_verified_at).toLocaleString() : '—'}
              </div>
              <div className="text-xs mt-1 text-emerald-200/80">
                We read back: <span className="font-mono text-emerald-100">{instance.verified_identifier || instance.tenant_id}</span>. If this doesn&apos;t match your venue, go back to step 2 and correct the details.
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleTest} disabled={!step1 || testing}
              className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="untill-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Plug className="w-4 h-4 mr-2"/>}
              {instance.verified ? 'Re-test connection' : 'Test connection'}
            </Button>
            {step2 && (
              <Button onClick={() => navigate('/quickbooks')} variant="outline"
                className="border-emerald-500/40 text-emerald-300 hover:text-white hover:bg-emerald-500/10">
                Continue to QuickBooks <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            )}
            {instance.configured && (
              <Button onClick={handleDisconnect} disabled={disconnecting} variant="outline"
                className="border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/20 ml-auto" data-testid="untill-disconnect-btn">
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Trash2 className="w-4 h-4 mr-2"/>}
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Where to find these */}
      <Card className="bg-slate-900/30 border-slate-800/60">
        <CardHeader>
          <CardTitle className="text-base font-heading text-white">Where do I find these details?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-3 leading-relaxed">
          <div>
            <div className="text-slate-200 font-medium mb-1">API base URL &amp; tenant name</div>
            <p>Open unTill Back Office → <span className="text-slate-300">Help</span> or <span className="text-slate-300">About</span>. Both the server address and the database name usually appear there. If nothing is listed, ask your unTill installer or email <span className="text-slate-300 font-mono">support@untill.com</span>.</p>
          </div>
          <div>
            <div className="text-slate-200 font-medium mb-1">Username &amp; password</div>
            <p>Use the same login you use for the unTill Back Office. For safety we recommend creating a dedicated integration user with permission to read sales, products, customers and inventory.</p>
          </div>
          <div>
            <div className="text-slate-200 font-medium mb-1">AppToken &amp; AppName</div>
            <p>These are issued by unTill to identify SyncPoint as an approved integrator. If you don&apos;t already have them, request them from your unTill partner contact or email <span className="text-slate-300 font-mono">integrations@untill.com</span>. Mention that you&apos;re connecting to SyncPoint.</p>
          </div>
          <div>
            <div className="text-slate-200 font-medium mb-1">Still stuck?</div>
            <p>Save what you have with the &quot;Save details&quot; button — you can come back and finish this step later. Nothing else will start syncing until this connection is verified.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
